import { type PatientFile, usePatientFilesQuery } from '@/services/patient-files'
import { borderRadius, colors } from '@/theme'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'

interface PreviewPatientFileDialogProps {
  open: boolean
  patientHash: string
  file: PatientFile | null
  onClose: () => void
}

export default function PreviewPatientFileDialog({ open, patientHash, file, onClose }: PreviewPatientFileDialogProps) {
  const { refetch } = usePatientFilesQuery(patientHash)
  // Presigned URLs expire after 300 s, so refetch on open and pin the fresh URL
  // in state — pinning keeps the img/iframe from reloading if the feed's
  // background refresh lands while the preview is up.
  const [freshFile, setFreshFile] = useState<PatientFile | null>(null)
  const [renderFailed, setRenderFailed] = useState(false)

  useEffect(() => {
    if (!open || !file) return
    let cancelled = false
    setFreshFile(null)
    setRenderFailed(false)
    refetch().then(({ data }) => {
      if (cancelled) return
      // If the refetch failed, the URL we already have is usually still within
      // its expiry window — better to try it than to show nothing.
      setFreshFile(data?.find(candidate => candidate.id === file.id) ?? file)
    })
    return () => {
      cancelled = true
    }
  }, [open, file, refetch])

  const displayName = file?.originalFilename ?? 'File'
  const mimeType = (freshFile ?? file)?.mimeType ?? ''
  const isPdf = mimeType === 'application/pdf'
  const isImage = mimeType.startsWith('image/')
  const url = (freshFile ?? file)?.url

  const handleOpenInNewTab = () => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{ sx: { borderRadius: borderRadius.md, height: '85vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pb: 1 }}>
        <Typography variant='sectionTitle' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title='Open in new tab'>
            <IconButton size='small' onClick={handleOpenInNewTab}>
              <OpenInNewIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <IconButton size='small' onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {!freshFile ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : renderFailed || (!isImage && !isPdf) ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}
          >
            <Typography sx={{ fontSize: 14, color: colors.text.secondary }}>
              This file can't be previewed in the browser.
            </Typography>
            <Button variant='secondary' startIcon={<OpenInNewIcon />} onClick={handleOpenInNewTab}>
              Open in new tab
            </Button>
          </Box>
        ) : isPdf ? (
          <Box
            component='iframe'
            src={freshFile.url}
            title={displayName}
            sx={{ flex: 1, width: '100%', border: `1px solid ${colors.border.default}`, borderRadius: 1 }}
          />
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
            <Box
              component='img'
              src={freshFile.url}
              alt={displayName}
              onError={() => setRenderFailed(true)}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 1 }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
